import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import { Feather, Mail, ArrowLeft, Check } from 'lucide-react';

import { API } from '../config';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
      toast.success('Check your email for reset instructions');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen bg-parchment flex items-center justify-center p-8">
        <div className="w-full max-w-md animate-fade-in text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="font-playfair text-3xl text-charcoal mb-4">Check Your Email</h1>
          <p className="font-lora text-charcoal-muted mb-8">
            If an account exists for <strong>{email}</strong>, you'll receive a password reset link shortly.
          </p>
          <p className="font-lora text-sm text-charcoal-light mb-6">
            Don't see it? Check your spam folder.
          </p>
          <Link 
            to="/login"
            className="font-lora text-sepia hover:text-wine transition-colors"
          >
            ← Back to login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-parchment flex items-center justify-center p-8">
      <div className="w-full max-w-md animate-fade-in">
        <Link 
          to="/login" 
          className="inline-flex items-center gap-2 text-charcoal-muted hover:text-charcoal transition-colors mb-8"
          data-testid="back-to-login"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="font-lora">Back to login</span>
        </Link>

        <div className="flex items-center gap-3 mb-8">
          <Feather className="w-8 h-8 text-wine" />
          <span className="font-playfair text-2xl text-charcoal">Unspoken Hearts</span>
        </div>

        <h1 className="font-playfair text-4xl text-charcoal mb-2">Forgot Password?</h1>
        <p className="font-lora text-charcoal-muted mb-8">
          Enter your email and we'll send you a link to reset your password.
        </p>

        <Card className="card-letter">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="font-lora text-charcoal">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-charcoal-light" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-underline pl-10 py-3"
                  placeholder="your@email.com"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <Button 
              type="submit" 
              className="btn-primary w-full justify-center"
              disabled={loading}
              data-testid="reset-submit-btn"
            >
              {loading ? 'Sending...' : 'Send Reset Link'}
            </Button>
          </form>
        </Card>

        <p className="text-center mt-6 font-lora text-charcoal-muted">
          Remember your password?{' '}
          <Link to="/login" className="text-sepia hover:text-wine transition-colors">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
